import { DialogM } from "@common/components/alert/dialog/Dialog"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { isSupersededResponse } from "@data/redux/reducers/api/pagedRequest"
import React, { forwardRef, useContext, useEffect, useRef } from "react"
import { Dimensions, FlatList, ScrollView, View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { useDispatch, useSelector } from "react-redux"
import TextM from "../components/text/TextM"
import { theme } from "../theme/theme"
import { DimensionsContext, IS_WEB } from "../utils/mobileUtils"

export const PaginatedFlatListContent = forwardRef((props, ref) => {
    const {
        data,
        ListHeaderComponent = <></>,
        renderItem,
        style,
        requestApiService,
        requestRouteParams,
        requestUrlParams,
        requestData,
        contentContainerStyle,
        tooManyColumns = false,
        ListEmptyComponent,
        noRefreshWhenNoResults = false,
        noActivityIndicator = false,
        totalCount,
        setTotalCount,
        currentPage,
        setCurrentPage,
        lastPage,
        setLastPage,
        refresh,
        refreshing,
        stickyHeader = false,
    } = props

    // Des filtres qui changent rechargent la page 1, même si la première réponse n'est pas encore
    // arrivée (ou a échoué) : sans quoi la liste resterait celle d'avant sous un filtre affiché.
    // Comparés par valeur, pas par identité ; le premier rendu est laissé au chargement initial.
    const urlParamsKey = JSON.stringify(requestUrlParams ?? null)
    const urlParamsMountedRef = useRef(false)
    useEffect(() => {
        if (!urlParamsMountedRef.current) {
            urlParamsMountedRef.current = true
            return
        }
        if (noRefreshWhenNoResults) {
            refresh()
        }
    }, [urlParamsKey])

    useEffect(() => {
        if (totalCount !== -1) {
            refresh()
        }
    }, [requestUrlParams?.search])

    const renderList = () => {
        return (
            <>
                {!noActivityIndicator &&
                    IS_WEB &&
                    refreshing &&
                    currentPage === 1 && (
                        <ActivityIndicator
                            color={theme.colors.primary}
                            style={{ marginVertical: 20 }}
                        />
                    )}
                <FlatList
                    data={totalCount === 0 && data?.length === 0 ? [] : data}
                    ref={ref}
                    refreshing={refreshing && currentPage === 1}
                    onRefresh={refresh}
                    style={{
                        width: dimensions?.width,
                        ...style,
                    }}
                    contentContainerStyle={{
                        alignItems: "center",
                        ...contentContainerStyle,
                    }}
                    stickyHeaderIndices={stickyHeader ? [0] : null}
                    ListHeaderComponent={() => (
                        <>
                            {renderHeader()}
                            {ListEmptyComponent
                                ? ListEmptyComponent
                                : totalCount === 0 &&
                                  !refreshing &&
                                  data?.length === 0 && (
                                      <TextM
                                          style={{
                                              textAlign: "center",
                                              marginTop: 10,
                                          }}
                                      >
                                          Aucun élément
                                      </TextM>
                                  )}
                        </>
                    )}
                    ListFooterComponent={renderFooter}
                    keyExtractor={(item, index) => index.toString()}
                    onEndReachedThreshold={0.2}
                    onEndReached={() => {
                        loadMore()
                    }}
                    renderItem={renderItem}
                />
            </>
        )
    }

    const success = useSelector(
        (state) => state.api[requestApiService?.name]?.success
    )
    const error = useSelector(
        (state) => state.api[requestApiService?.name]?.error
    )
    const successData = useSelector(
        (state) => state.api[requestApiService?.name]?.data
    )
    const successRouteParams = useSelector(
        (state) => state.api[requestApiService?.name]?.routeParams
    )
    const successUrlParams = useSelector(
        (state) => state.api[requestApiService?.name]?.urlParams
    )
    const dispatch = useDispatch()

    const loadMore = () => {
        if (!refreshing && currentPage < lastPage && data?.length > 0) {
            dispatch(
                apiRequest(
                    requestApiService,
                    requestRouteParams ?? {},
                    requestData ?? {},
                    {
                        ...requestUrlParams,
                        page: currentPage + 1,
                    }
                )
            )
            setCurrentPage((p) => p + 1)
        }
    }

    useEffect(() => {
        if (!refreshing) {
            refresh()
        }
    }, [])

    useEffect(() => {
        if (success) {
            dispatch(apiIdle(requestApiService))
            // Une liste qui se recharge à chaque changement de filtres ne prend pas son curseur
            // d'une réponse à des filtres qu'elle n'a plus.
            const superseded =
                noRefreshWhenNoResults &&
                isSupersededResponse(
                    {
                        routeParams: successRouteParams,
                        urlParams: successUrlParams,
                    },
                    requestRouteParams,
                    requestUrlParams
                )
            if (!superseded) {
                setCurrentPage(successData?.meta?.current_page)
                setLastPage(successData?.meta?.last_page)
                setTotalCount(successData?.meta?.total)
            }
        }
        if (error) {
            let errorMessages = []
            if (error?.errors) {
                errorMessages = Object.keys(error?.errors)?.map(
                    (key) => error?.errors[key][0]
                )
            }
            DialogM.show({
                text1: "Erreur",
                text2:
                    error?.message +
                    (errorMessages ? "\n" + errorMessages.join("\n") : ""),
            })
            dispatch(apiIdle(requestApiService))
        }
    }, [success, error])
    const dimensions = useContext(DimensionsContext)

    const renderFooter = () => {
        // It will show indicator at the bottom of the list when data is loading otherwise it returns null
        return (
            <View
                style={{
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {refreshing && currentPage > 1 && (
                    <ActivityIndicator
                        animating={true}
                        size="small"
                        style={{ marginVertical: 5 }}
                        color={theme.colors.primary}
                    />
                )}

                <View
                    style={{
                        width: Dimensions.get("window").width,
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        paddingHorizontal: 10,
                        marginBottom: 100,
                    }}
                >
                    {data?.length > 0 && totalCount > 0 && (
                        <TextM
                            color={theme.colors.grey}
                            fontSize={12}
                            fontWeight="Light"
                        >
                            {data?.length} / {totalCount}
                        </TextM>
                    )}
                </View>
            </View>
        )
    }

    const renderHeader = () => {
        return ListHeaderComponent
    }

    return (
        <>
            {tooManyColumns ? (
                <ScrollView
                    horizontal={true}
                    contentContainerStyle={{
                        alignItems: "flex-start",
                        justifyContent: "center",
                    }}
                    style={{
                        backgroundColor: theme.colors.whiteAlmost,
                        width: Dimensions.get("window").width,
                        flex: 1,
                    }}
                >
                    <View
                        style={{
                            height: "100%",
                        }}
                    >
                        {renderList()}
                    </View>
                </ScrollView>
            ) : (
                renderList()
            )}
        </>
    )
})
